// ─────────────────────────────────────────────────────────────────────────────
// BORDS — Fabric Scorecard Data  (Academy India 2026)
// Single source of truth consumed by:
//   OurFabricsPage, ProductCard badge, ProductDetailPage Fabric tab,
//   CertificationsPage.
//
// Score bands:
//   Buy Freely   8–10  → --bords-moss  #4A5D3A
//   Certify It   6–7.9 → --bords-moss  (slightly lighter)
//   Keep It Long 4–5.9 → --bords-clay  #C1613B
//   Rarely       2–3.9 → --bords-rust  #8C2F1D
//   Avoid        0–1.9 → --bords-rust  (full intensity)
// ─────────────────────────────────────────────────────────────────────────────

export const FABRIC_BANDS = [
  { id: 'buy-freely',    label: 'Buy Freely',    min: 8,   max: 10,  color: '#384F2A', bg: 'rgba(56,79,42,0.12)',   border: 'rgba(56,79,42,0.32)',   darkColor: '#88D66C', darkBg: 'rgba(136,214,108,0.18)', darkBorder: 'rgba(136,214,108,0.45)' },
  { id: 'certify-it',   label: 'Certify It',    min: 6,   max: 7.9, color: '#384F2A', bg: 'rgba(56,79,42,0.09)',   border: 'rgba(56,79,42,0.25)',   darkColor: '#9FD982', darkBg: 'rgba(159,217,130,0.18)', darkBorder: 'rgba(159,217,130,0.42)' },
  { id: 'keep-it-long', label: 'Keep It Long',  min: 4,   max: 5.9, color: '#B5542E', bg: 'rgba(181,84,46,0.12)',  border: 'rgba(181,84,46,0.30)',  darkColor: '#FFA578', darkBg: 'rgba(255,165,120,0.20)', darkBorder: 'rgba(255,165,120,0.45)' },
  { id: 'rarely',       label: 'Rarely',         min: 2,   max: 3.9, color: '#852615', bg: 'rgba(133,38,21,0.12)',  border: 'rgba(133,38,21,0.30)',  darkColor: '#FF7A66', darkBg: 'rgba(255,122,102,0.20)', darkBorder: 'rgba(255,122,102,0.45)' },
  { id: 'avoid',        label: 'Avoid',           min: 0,   max: 1.9, color: '#852615', bg: 'rgba(133,38,21,0.18)',  border: 'rgba(133,38,21,0.42)',  darkColor: '#FF6B55', darkBg: 'rgba(255,107,85,0.24)',  darkBorder: 'rgba(255,107,85,0.50)' },
];

/** Return the band object for a given numeric score with optional dark-mode contrast adapt. */
export function getBand(score, isDark = false) {
  const base = FABRIC_BANDS.find(b => score >= b.min && score <= b.max) || FABRIC_BANDS[4];
  if (!isDark) return base;
  return {
    ...base,
    color: base.darkColor || base.color,
    bg: base.darkBg || base.bg,
    border: base.darkBorder || base.border
  };
}

// ── Active BORDS fabrics (what we source) ────────────────────────────────────
export const BORDS_FABRICS = [
  {
    id: 'organic-cotton-gots',
    name: 'Organic Cotton',
    qualifier: 'GOTS Certified',
    score: 7.0,
    verdictShort: 'Certify It',
    verdictLong:
      'Grown without synthetic pesticides or fertilisers. GOTS certification ' +
      'verifies organic status from farm to finished garment — check the certificate ' +
      'number before paying any eco premium. Water use is still significant; care by ' +
      'washing cold and air-drying.',
    fibre: '100% Organic Cotton',
    careNote: 'Wash 30 °C cold, air dry, no tumble dry.',
    sourceIndia:
      'Any GOTS-licensed mill — demand the certificate number and cross-check at ' +
      'global-standard.org before placing an order.',
    certBody: 'GOTS (Global Organic Textile Standard)',
    certNumber: 'PENDING — apply before launch',
    certVerifyUrl: 'https://global-standard.org/find-suppliers-shops/database',
    ratePerM: 280,
    skuNote: 'Core drop (margin) — everyday price point, GOTS badge on tag.',
    active: true,
  },
  {
    id: 'recycled-cotton',
    name: 'Recycled Cotton',
    qualifier: 'GRS Certified',
    score: 8.5,
    verdictShort: 'Buy Freely',
    verdictLong:
      'Diverts pre- or post-consumer cotton waste from landfill. Requires ' +
      'significantly less water and energy than virgin cotton. GRS certification ' +
      'tracks recycled content through the supply chain — ask for the certificate ' +
      'number at every tier.',
    fibre: '100% Recycled Cotton',
    careNote: 'Wash 30 °C cold, air dry. Avoid bleach.',
    sourceIndia:
      'Panipat, Haryana — world\'s largest recycled-cotton cluster. ' +
      'Ask for GRS certificate number; verify at textileexchange.org.',
    certBody: 'GRS (Global Recycled Standard)',
    certNumber: 'PENDING — apply before launch',
    certVerifyUrl: 'https://textileexchange.org/integrity/',
    ratePerM: 200,
    skuNote: 'Value drop (volume) — cheapest per-unit footprint and cost, use for logo basics.',
    active: true,
  },
  {
    id: 'khadi',
    name: 'Khadi',
    qualifier: 'Khadi Mark / KVIC',
    score: 8.5,
    verdictShort: 'Buy Freely',
    verdictLong:
      'Hand-spun and hand-woven. Zero industrial energy in spinning and weaving. ' +
      'Supports rural artisan livelihoods and the oldest sustainable textile tradition in India. ' +
      'Accept the Khadi Mark (KVIC) only — reject machine-made imitations sold as khadi.',
    fibre: '100% Hand-spun, Hand-woven Cotton Khadi',
    careNote: 'Hand wash cold. Do not wring. Dry in shade. Iron on low heat.',
    sourceIndia:
      'Khadi Gramodyog Bhavan outlets — accept KVIC Khadi Mark only. ' +
      'Verify at kvic.gov.in.',
    certBody: 'Khadi Mark (KVIC — Khadi and Village Industries Commission)',
    certNumber: 'PENDING — apply before launch',
    certVerifyUrl: 'https://www.kvic.gov.in',
    ratePerM: 350,
    skuNote: 'Story drop (limited ~7 pcs) — heritage narrative, highest price point.',
    active: true,
  },
];

// ── Do-not-source list ────────────────────────────────────────────────────────
export const AVOID_FABRICS = [
  {
    name: 'Virgin Polyester',
    score: 2.0,
    reason: 'Fossil feedstock. Sheds microplastic fibres permanently with every wash.',
  },
  {
    name: 'Nylon',
    score: 1.5,
    reason: 'Production emits N₂O — a greenhouse gas 273× more potent than CO₂.',
  },
  {
    name: 'Acrylic',
    score: 1.0,
    reason: 'Heaviest microfibre shedder measured. Not recyclable at end of life.',
  },
  {
    name: 'PVC "Vegan Leather"',
    score: 0.5,
    reason: 'Contains phthalates and dioxins. Garment life ~2 years.',
  },
  {
    name: 'Poly-Cotton Blend',
    score: 1.5,
    reason: 'Blended construction makes it unrecyclable — carries both footprints.',
  },
  {
    name: 'Uncertified Viscose / "Bamboo Fabric"',
    score: 3.0,
    reason: 'Chemical rayon process marketed as natural. No cert = treat as conventional.',
  },
  {
    name: 'Elastane in non-stretch garments',
    score: 1.0,
    reason: 'Even 2% elastane blocks recyclability of the entire garment.',
  },
];

// ── Lookup helpers ────────────────────────────────────────────────────────────
/** Return a fabric by its `id` string, or null if not found. */
export function getFabricById(id) {
  return BORDS_FABRICS.find(f => f.id === id) || null;
}

/** Options array for AdminPanel dropdown: [{ value, label }, ...] */
export const FABRIC_OPTIONS = [
  { value: '', label: '— None / Uncertified —' },
  ...BORDS_FABRICS.map(f => ({
    value: f.id,
    label: `${f.name} (${f.qualifier}) — Score ${f.score}`,
  })),
];
